import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAgents, useDeleteAgent, useLatestAgentVersion, useUpdateAgent } from '@/hooks/useAgents';
import { AgentBadge } from '@/components/common/StatusBadge';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { ApiError } from '@/api/client';
import { isAgentOnline, relTime, shortId } from '@/lib/format';

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed');

function VersionCell({ version, latest }: { version: string | null; latest: string | null | undefined }) {
  const v = version ?? '—';
  if (!version || !latest) return <span className="mono dim">{v}</span>;
  const current = version === latest;
  return (
    <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="dim">{v}</span>
      <span
        className="mono"
        style={{
          fontSize: 10, letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 999,
          color: current ? 'var(--signal)' : 'var(--warn)',
          border: `1px solid ${current ? 'rgba(84,240,168,0.35)' : 'rgba(255,194,75,0.4)'}`,
        }}
      >
        {current ? 'current' : 'update available'}
      </span>
    </span>
  );
}

export function Agents() {
  const { data, isLoading, error } = useAgents();
  const latest = useLatestAgentVersion();
  const del = useDeleteAgent();
  const upd = useUpdateAgent();
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const agents = data ?? [];
  const offline = agents.filter((a) => !isAgentOnline(a.last_seen_at));
  const latestV = latest.data?.version ?? null;

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="eyebrow">fleet</div>
          <div className="title">Agents</div>
          <div className="sub">
            Enrolled test agents. Online = phoned home within 30s.
            {latestV && <> · latest build <span className="mono signal">{latestV}</span></>}
          </div>
        </div>
        {agents.length > 0 && (
          <div className="card kpi" style={{ minWidth: 130, borderColor: offline.length ? 'rgba(255,194,75,0.35)' : undefined }}>
            <div className="k-label">{offline.length ? '⚠ offline' : 'all online'}</div>
            <div className="k-value" style={{ fontSize: 24, color: offline.length ? 'var(--warn)' : 'var(--signal)' }}>
              {offline.length ? `${offline.length}/${agents.length}` : agents.length}
            </div>
          </div>
        )}
      </div>

      {offline.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--warn)', padding: '10px 14px' }}>
          <span className="mono" style={{ fontSize: 13, color: 'var(--warn)' }}>
            ⚠ {offline.length} agent{offline.length > 1 ? 's' : ''} offline: {offline.map((a) => a.label || shortId(a.external_id)).join(', ')}
          </span>
        </div>
      )}
      {note && <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--signal)', padding: '10px 14px' }}><span className="mono" style={{ fontSize: 13 }}>{note}</span></div>}
      {err && <div style={{ marginBottom: 16 }} onClick={() => setErr(null)}><ErrorBanner error={err} /></div>}

      {isLoading ? (
        <Loading label="Loading agents" />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : agents.length === 0 ? (
        <EmptyState title="No agents enrolled" hint="Run an agent binary on a device (it prompts for email/password) to enroll it." />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th>Status</th><th>Label</th><th>Platform</th><th>Version</th>
                <th>Last IP</th><th>Last seen</th><th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => {
                const online = isAgentOnline(a.last_seen_at);
                const behind = !!latestV && !!a.agent_version && a.agent_version !== latestV;
                const updating = upd.isPending && upd.variables === a.id;
                return (
                  <tr key={a.id} style={{ opacity: online ? 1 : 0.6 }}>
                    <td><AgentBadge online={online} /></td>
                    <td>
                      <Link to={`/agents/${a.id}`} className="mono" style={{ color: 'var(--signal)' }}>
                        {a.label || shortId(a.external_id)}
                      </Link>
                      <div className="muted mono" style={{ fontSize: 11 }}>{shortId(a.id)}</div>
                    </td>
                    <td className="mono dim">{a.platform ?? '—'}</td>
                    <td><VersionCell version={a.agent_version} latest={latestV} /></td>
                    <td className="mono dim">{a.last_ip ?? '—'}</td>
                    <td className="dim">{relTime(a.last_seen_at)}</td>
                    <td className="right">
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-ghost"
                          disabled={!online || updating}
                          title={!online ? 'agent is offline' : behind ? 'update to the latest build' : 're-check / update'}
                          style={{ color: behind ? 'var(--warn)' : undefined }}
                          onClick={() => {
                            setErr(null);
                            upd.mutate(a.id, {
                              onSuccess: () => setNote(`Update requested for ${a.label || shortId(a.id)}. It will download, verify, re-exec, and reconnect with the new version.`),
                              onError: (e) => setErr(msgOf(e)),
                            });
                          }}
                        >
                          {updating ? <span className="spinner" /> : 'Update'}
                        </button>
                        <button
                          className="btn btn-ghost"
                          style={{ color: 'var(--loss)' }}
                          disabled={del.isPending}
                          onClick={() => { if (confirm(`Delete agent ${a.label || shortId(a.id)}?`)) del.mutate(a.id); }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
